export type ApiRequestOutcome =
  | 'success'
  | 'http_error'
  | 'timeout'
  | 'cancelled'
  | 'network_error';

export type ApiRequestPerformanceEvent = {
  kind: 'api_request';
  requestKind: 'authenticated' | 'public';
  method: string;
  routeFamily: string;
  platform: string;
  durationMs: number;
  outcome: ApiRequestOutcome;
  attempt: number;
  statusCode?: number;
  correlationId: string;
};

export type ApiPerformanceObserver = (event: Readonly<ApiRequestPerformanceEvent>) => void;

let observer: ApiPerformanceObserver | null = null;
let successSampleRate = 0.1;

/**
 * Registers a best-effort observer for central API timings. It is disabled by
 * default and must never throw into a product request.
 */
export function setApiPerformanceObserver(
  next: ApiPerformanceObserver | null,
  options?: { successSampleRate?: number },
): void {
  observer = next;
  if (options?.successSampleRate !== undefined) {
    successSampleRate = Math.min(1, Math.max(0, options.successSampleRate));
  }
}

/**
 * Keeps only the stable API resource family. Query strings, hashes and every
 * identifier/action segment are discarded to prevent PII and high cardinality.
 */
export function toApiRouteFamily(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] ?? '';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return '/';
  if (segments[0]?.toLowerCase() !== 'api') return '/other';
  const resource = segments[1]?.toLowerCase();
  return resource && /^[a-z0-9-]+$/.test(resource) ? `/api/${resource}` : '/api/other';
}

export function emitApiRequestPerformance(
  event: ApiRequestPerformanceEvent,
  random: () => number = Math.random,
): void {
  if (!observer) return;
  if (event.outcome === 'success' && random() >= successSampleRate) return;
  try {
    observer(Object.freeze({ ...event }));
  } catch {
    // Telemetry is best-effort and must never affect the request lifecycle.
  }
}
